import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, buildWaLink } from "../lib/api";

// Slideshow — CEO Travel HD Batam photos
const SLIDES = [
  {
    img: "/batam/pink-beach.jpg",
    label: { id: "Pink Sand Beach · Batam", en: "Pink Sand Beach · Batam", zh: "粉红沙滩 · 巴淡", ja: "ピンクサンドビーチ · バタム", ko: "핑크 샌드 비치 · 바탐", vi: "Bãi cát hồng · Batam", th: "หาดทรายสีชมพู · บาตัม" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/barelang.jpg",
    label: { id: "Jembatan Barelang · Ikon Batam", en: "Barelang Bridge · Batam Icon", zh: "巴雷朗大桥 · 巴淡地标", ja: "バレラン橋 · バタムのシンボル", ko: "바렐랑 다리 · 바탐 랜드마크", vi: "Cầu Barelang · Biểu tượng Batam", th: "สะพานบาเรลัง · สัญลักษณ์บาตัม" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/mangrove.jpg",
    label: { id: "Pandang Tak Jemu · Mangrove Walk", en: "Mangrove Boardwalk · Batam", zh: "红树林木栈道 · 巴淡", ja: "マングローブ遊歩道 · バタム", ko: "맹그로브 산책로 · 바탐", vi: "Đường dạo rừng ngập mặn · Batam", th: "ทางเดินป่าโกงกาง · บาตัม" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/nongsa.jpg",
    label: { id: "Pantai Nongsa · Sunset Terbaik", en: "Nongsa Beach · Best Sunset", zh: "农沙海滩 · 最美日落", ja: "ノンサビーチ · 絶景の夕日", ko: "농사 해변 · 최고의 일몰", vi: "Bãi biển Nongsa · Hoàng hôn đẹp nhất", th: "หาดนองซา · พระอาทิตย์ตกสวยสุด" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/welcome.jpg",
    label: { id: "Welcome to Batam · Spot Ikonik", en: "Welcome to Batam · Iconic Landmark", zh: "欢迎来到巴淡 · 必打卡地标", ja: "ウェルカム・トゥ・バタム · 必訪スポット", ko: "웰컴 투 바탐 · 명소", vi: "Welcome to Batam · Điểm check-in", th: "ป้ายต้อนรับบาตัม · จุดถ่ายรูปสัญลักษณ์" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/kelong-sunset.jpg",
    label: { id: "Floating Restaurant · Sunset Magic", en: "Floating Restaurant · Sunset Magic", zh: "海上漂浮餐厅 · 醉美夕阳", ja: "フローティングレストラン · 夕日マジック", ko: "플로팅 레스토랑 · 황홀한 일몰", vi: "Nhà hàng nổi · Hoàng hôn ma mị", th: "ร้านอาหารลอยน้ำ · พระอาทิตย์ตกสุดมหัศจรรย์" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/pink-pool.jpg",
    label: { id: "Pink Beach Club · Lifestyle Hits", en: "Pink Beach Club · Lifestyle Hotspot", zh: "粉红海滩俱乐部 · 网红打卡", ja: "ピンクビーチクラブ · 大人の隠れ家", ko: "핑크 비치 클럽 · 라이프스타일 명소", vi: "Pink Beach Club · Điểm check-in hot", th: "พิงค์บีชคลับ · ไลฟ์สไตล์ฮอตสปอต" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/ferry.jpg",
    label: { id: "Ferry Batam · Antar Pulau", en: "Batam Ferry · Inter-Island", zh: "巴淡渡轮 · 跨岛之旅", ja: "バタムフェリー · 島めぐり", ko: "바탐 페리 · 섬 간 이동", vi: "Phà Batam · Kết nối các đảo", th: "เรือเฟอร์รี่บาตัม · ข้ามเกาะ" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/seafood.jpg",
    label: { id: "Fresh Seafood · Kuliner Khas", en: "Fresh Seafood · Local Cuisine", zh: "新鲜海鲜 · 本地美食", ja: "新鮮シーフード · 地元グルメ", ko: "신선한 해산물 · 현지 미식", vi: "Hải sản tươi · Ẩm thực địa phương", th: "อาหารทะเลสด · อาหารท้องถิ่น" },
    flag: "🇮🇩",
  },
  {
    img: "/batam/dining.jpg",
    label: { id: "Dining Experience · Authentic Batam", en: "Dining Experience · Authentic Batam", zh: "用餐体验 · 地道巴淡", ja: "ダイニング体験 · 本場バタム", ko: "다이닝 경험 · 정통 바탐", vi: "Trải nghiệm ẩm thực · Batam đích thực", th: "ประสบการณ์มื้ออาหาร · บาตัมแท้" },
    flag: "🇮🇩",
  },
];

const SLIDE_MS = 6000;

// Teks di bawah QR
const QR_TEXT = {
  id: "Scan untuk chat WhatsApp",
  en: "Scan to chat on WhatsApp",
  zh: "扫码联系 WhatsApp",
  ja: "スキャンして WhatsApp へ",
  ko: "스캔하여 WhatsApp 문의",
  vi: "Quét mã để chat WhatsApp",
  th: "สแกนเพื่อแชท WhatsApp",
};

export const Hero = () => {
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const [logoUrl, setLogoUrl] = useState("/logo-hero.png");
  const wa = buildWaLink(t.waMessages.consult);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let mounted = true;
    api
      .get("/settings/branding")
      .then((r) => mounted && r.data?.logo_url && setLogoUrl(r.data.logo_url))
      .catch((err) => console.warn("branding fetch failed", err));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-screen min-h-[640px] w-full overflow-hidden"
    >
      {/* Slideshow background */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={idx}
            className="absolute inset-0 bg-cover bg-center will-change-transform"
            style={{ backgroundImage: `url(${SLIDES[idx].img})` }}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1.15 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.6, ease: "easeInOut" },
              scale: { duration: SLIDE_MS / 1000 + 2, ease: "linear" },
            }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C2230]/70 via-[#0C2230]/40 to-[#0C2230]/85" />
      </div>

      {/* Slide chip (top-right area, below navbar) */}
      <div className="absolute right-6 top-24 z-10 md:right-12 lg:right-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={`chip-${idx}`}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-medium text-white backdrop-blur-md"
          >
            <span className="text-base">{SLIDES[idx].flag}</span>
            <span data-testid="hero-slide-label">
              {SLIDES[idx].label[lang] || SLIDES[idx].label.en}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ===== QR WhatsApp (desktop/tablet saja) ===== */}
      <motion.a
        href={wa}
        target="_blank"
        rel="noreferrer"
        data-testid="hero-wa-qr"
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-6 top-40 z-20 hidden flex-col items-center gap-3 rounded-3xl border border-white/25 bg-white/10 p-4 backdrop-blur-md transition-all hover:bg-white/20 hover:shadow-2xl md:right-12 md:top-44 md:flex lg:right-16 lg:p-5"
      >
        <span className="rounded-2xl bg-white p-2.5 shadow-lg">
          <img
            src="/services/wa-qr.png"
            alt="WhatsApp QR CEO Travel"
            className="h-32 w-32 object-contain lg:h-40 lg:w-40"
          />
        </span>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-white drop-shadow">
          <MessageCircle className="h-3.5 w-3.5 text-[#0E9A57]" />
          {QR_TEXT[lang] || QR_TEXT.en}
        </span>
      </motion.a>

      {/* Logo aligned with navbar menu level */}
      <motion.img
        src={logoUrl}
        alt="CEO Travel"
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-brand-logo"
        className="absolute left-6 top-3 z-[60] h-10 w-auto object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.55)] md:left-12 md:top-4 md:h-12 lg:left-16 lg:h-14"
      />

      {/* Foreground content */}
      <div className="relative z-10 flex h-full flex-col items-start justify-end px-6 pb-24 pt-32 md:px-16 lg:px-24">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-white/80"
        >
          <span className="inline-block h-px w-10 bg-[#B7892F]" />
          {t.hero.eyebrow}
        </motion.span>

        {/* Judul disembunyikan secara visual, tetap ada untuk SEO */}
        <h1 className="sr-only">
          {t.hero.title1} {t.hero.title2} {t.hero.title3}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-8 max-w-2xl text-base text-white/80 md:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#paket"
            data-testid="hero-view-packages-button"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#0C2230] transition-all hover:bg-[#FBFAF6] hover:shadow-xl"
          >
            {t.hero.cta1}
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#0E9A57] px-7 py-3.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#0c8a4d] hover:shadow-xl"
          >
            <MessageCircle className="h-4 w-4" />
            {t.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-white/70"
        >
          <span data-testid="hero-stat-experience">{t.hero.stat1}</span>
          <span className="hidden h-px w-6 bg-white/30 sm:inline-block" />
          <span data-testid="hero-stat-rating">{t.hero.stat2}</span>
          <span className="hidden h-px w-6 bg-white/30 sm:inline-block" />
          <span data-testid="hero-stat-support">{t.hero.stat3}</span>
        </motion.div>

        {/* Slide indicators (bottom-left) */}
        <div className="mt-10 flex items-center gap-1.5">
          {SLIDES.map((slide, i) => (
            <button
              key={slide.img}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`slide ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === idx ? "w-8 bg-[#B7892F]" : "w-4 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
